import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ISTPersonActivity } from 'aa-ng';
import { map, Observable, of } from 'rxjs';
import {
  ICollaborationActivityRef,
  ICollaborationGraph,
  ICollaborationGraphRequest,
  IEdsCollaborationGraphRequest,
} from 'src/app/models';

import { LoadingIndicatorService } from './loading-indicator.service';

/**
 * Every call to the collaboration-graph endpoints, in one place.
 *
 * Mirrors the server: `CollaborationGraphController` owns these three actions, so one Angular service
 * owns them too. They previously sat on `PeopleService`, `PersonSearchService` and
 * `ActivitySearchService` - three services calling a fourth controller, which made the pathway hard to
 * find and left each caller to remember its own caching rules. AMS is arranged the same way.
 */
@Injectable({ providedIn: 'root' })
export class CollaborationGraphService {
  private http = inject(HttpClient);
  private loadingIndicatorService = inject(LoadingIndicatorService);

  private _baseUrl = '/api/collaboration-graph';

  /**
   * How many profile graphs to keep. The point of the cache is to make sliding the collaborators
   * control back to a value you already looked at free, so it only has to hold the recent past; this
   * is a few profiles' worth of slider positions.
   */
  private static readonly BY_PERSON_CACHE_LIMIT = 24;

  /**
   * Recently fetched profile graphs, keyed on guid *and* on the bound asked for - the same person at a
   * different collaborator count is a different graph.
   *
   * A Map rather than an object because insertion order gives eviction for free. It is bounded because
   * the key includes a slider position: unbounded, a long session that browses profiles and nudges the
   * slider on each accumulates an entry per profile x position and never releases one. Entries are
   * never invalidated, so a graph whose underlying data changed stays stale until it is evicted or the
   * page is reloaded.
   */
  private _byPersonCache = new Map<string, ICollaborationGraph>();

  /**
   * The topic-driven graph - the network search Related Networks tab and the relevancy panel, which
   * differ only in whether `seedPersonGuid` is set.
   *
   * The topic travels in the body. On the relevancy route it used to be an un-encoded path *segment*,
   * so a query containing `/ ? # %` 404'd.
   */
  getByTopic(
    request: IEdsCollaborationGraphRequest,
  ): Observable<ICollaborationGraph> {
    return this.loadingIndicatorService.fromObservable(
      this.http.post<ICollaborationGraph>(`${this._baseUrl}/by-topic`, request),
    );
  }

  /** The identity-driven graph: a scholar's profile collaborations. */
  getByPerson(
    personGuid: string,
    collaboratorsPerScholar: number,
  ): Observable<ICollaborationGraph> {
    const cacheKey = `${personGuid}?collaboratorsPerScholar=${collaboratorsPerScholar}`;

    if (this._byPersonCache.has(cacheKey)) {
      // Re-inserted so it counts as recently used, not merely recently fetched.
      const cached = this._byPersonCache.get(cacheKey);
      this._byPersonCache.delete(cacheKey);
      this._byPersonCache.set(cacheKey, cached);
      return of(cached);
    }

    // Typed rather than an inline literal: the body used to name a field the request contract does not
    // have, which posts happily and comes back unseeded. The type is the only thing that catches that.
    //
    // No institutionIds: EDS scopes every graph to the caller's institution group, resolved server-side
    // from the authorization context rather than accepted from the body.
    const request: ICollaborationGraphRequest = {
      seedPersonGuid: personGuid,
      collaboratorsPerScholar,
    };

    // Wrapped like getByTopic/getNodeActivities so a cache miss shows a spinner. Deliberately
    // only around the HTTP call - a cache hit resolves synchronously and must not flash one.
    return this.loadingIndicatorService
      .fromObservable(
        this.http.post<ICollaborationGraph>(
          `${this._baseUrl}/by-person`,
          request,
        ),
      )
      .pipe(
        map((graph) => {
          this._byPersonCache.set(cacheKey, graph);

          while (
            this._byPersonCache.size >
            CollaborationGraphService.BY_PERSON_CACHE_LIMIT
          ) {
            this._byPersonCache.delete(this._byPersonCache.keys().next().value);
          }

          return graph;
        }),
      );
  }

  /**
   * Turns the clicked node's activity ids into activity records.
   *
   * The graph's edges already name the works, so this is an id-to-record lookup rather than a second
   * graph build. It is a request rather than a filter over already-loaded activities because the
   * endpoint returns them with no contributors, which is what lets the activity list offer its paged
   * collaborator fetch - a locally filtered profile activity carries its contributors and suppresses
   * that fetch entirely.
   */
  getNodeActivities(
    personGuid: string,
    collaborations: ICollaborationActivityRef[],
  ): Observable<ISTPersonActivity[]> {
    return this.loadingIndicatorService.fromObservable(
      this.http.post<ISTPersonActivity[]>(`${this._baseUrl}/node-activities`, {
        personGuid,
        collaborations,
      }),
    );
  }
}
